<template>
  <div class="py-6 px-8">
    <h-header>
      <template v-slot:actions>
        <div class="d-flex justify-end">
          <v-btn @click="openCreateModalImob" color="primary" elevation="0">
            <v-icon left>mdi-plus</v-icon>
            <span class="d-none d-md-flex body-2">
              {{ $t(`hausefulUsers.action.createRealEstate`) }}
            </span>
          </v-btn>
        </div>
      </template>
    </h-header>

    <v-card outlined>
      <v-alert v-if="error" type="error">
        {{ error }}
      </v-alert>

      <v-data-table
        :loading-text="$t('appointment.table.loading')"
        :no-data-text="$t('appointment.table.noData')"
        :items="serviceProviderCustomers"
        :items-per-page="30"
        :loading="isLoading"
        :headers="headers"
        mobile-breakpoint="20"
        class="clickable"
        hide-default-footer
        disable-sort
      >
        <template v-slot:[`item.name`]="{ item }">
          {{ item.name || '-' }}
        </template>
        <template v-slot:[`item.cnpj`]="{ item }">
          <span class="grey--text text--darken-2">
            {{ formatCnpj(item.cnpj) }}
          </span>
        </template>

        <template v-slot:[`item.balance`]="{ item }">
          <span :class="balanceClass(item.balance)">
            {{ formatBalance(item.balance) }}
          </span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon style="height: 24px; width: 24px;">
                <v-icon size="16">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item
                @click="openCreateModal(item)"
                 class="py-1 px-4 text-body-2"
                 style="min-height: 40px;"              >
                <v-icon size="18" class="mr-2">mdi-file-document-outline</v-icon>
                <span>{{ $t('contract.actions.createContract') }}</span>
              </v-list-item>
              <v-list-item
                @click="openViewContract(item)"
                 class="py-1 px-4 text-body-2"
                 style="min-height: 40px;"              >
                <v-icon size="18" class="mr-2">mdi-file-document-outline</v-icon>
                <span>{{ $t('customer.label.visualizeContracts') }}</span>
              </v-list-item>
              <v-list-item
                @click="openUpdateModalImob(item)"
                 class="py-1 px-4 text-body-2"
                 style="min-height: 40px;"              >
                <v-icon size="18" class="mr-2">mdi-pencil-outline</v-icon>
                <span>{{ $t('defaultServiceValues.actions.update') }}</span>
              </v-list-item>
              <v-list-item
                @click="openAddBalanceModal(item)"
                class="py-1 px-4 text-body-2"
                style="min-height: 40px;"
              >
                <v-icon size="18" class="mr-2">mdi-account-cash-outline</v-icon>
                <span>{{ $t('movement.label.changeBalance') }}</span>
              </v-list-item>
              <v-list-item
                @click="confirmDelete(item)"
                 class="py-1 px-4 text-body-2"
                 style="min-height: 40px;"              >
                <v-icon size="18" class="mr-2" color="grey--text">mdi-delete-outline</v-icon>
                <span>{{ $t('appointment.additionalData.remove') }}</span>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

      <h-pagination :fetch="listServiceProviderCustomers" />
    </v-card>

    <create-contract-dialog
      v-model="dialogVisible"
      :client="selectedClient"
      @created="listServiceProviderCustomers"
    />

    <ViewContractModal :client="selectedClient" />

    <CreateUpdateServiceProviderRealEstate
      :client="selectedClient"
      @updated="listServiceProviderCustomers"
      @error="handleError"
    />

    <RemoveServiceProviderRealEstate
      :client="selectedClient"
      @success="listServiceProviderCustomers"
      @error="handleError"
    />

    <ServiceProviderAddBalanceModal
      :client="selectedClient"
      @success="listServiceProviderCustomers"
      @error="handleError"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { catalogMonetary } from '@/util/catalog'
import { maskCnpj } from '@/util/masks'
import CreateContractDialog from './CreateContractDialog.vue'
import ViewContractModal from './ViewContractModal.vue'
import CreateUpdateServiceProviderRealEstate from '../modals/CreateUpdateServiceProviderRealEstate.vue'
import RemoveServiceProviderRealEstate from '../modals/RemoveServiceProviderRealEstate.vue'
import ServiceProviderAddBalanceModal from '../modals/ServiceProviderAddBalanceModal.vue'

export default {
  name: 'ServiceProviderClientsList',

  components: {
    CreateContractDialog,
    ViewContractModal,
    CreateUpdateServiceProviderRealEstate,
    RemoveServiceProviderRealEstate,
    ServiceProviderAddBalanceModal,
  },

  data() {
    return {
      headers: [
        { text: this.$t('forms.completeName'), value: 'name', sortable: false },
        { text: this.$t('onboarding.register.cnpj'), value: 'cnpj', align: 'start', sortable: false, width: '220px' },
        { text: this.$t('movement.create.availableBalance'), value: 'balance', align: 'end', sortable: false, width: '180px' },
        { text: '', value: 'actions', align: 'end', sortable: false, width: '60px' },
      ],
      dialogVisible: false,
      selectedClient: null,
      error: null,
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'serviceProviderCustomers']),
  },

  methods: {
    ...mapActions([
      'listServiceProviderCustomers',
      'openModal'
    ]),

    formatCnpj(value) {
      if (!value) return '-'
      return maskCnpj(String(value)) || '-'
    },

    formatBalance(balance) {
      return catalogMonetary(balance || 0)
    },

    balanceClass(balance) {
      const value = Number(balance || 0)
      return value < 0 ? 'error--text font-weight-medium' : 'success--text font-weight-medium'
    },

    openCreateModalImob() {
      this.selectedClient = null
      this.openModal('CREATE_UPDATE_SERVICE_PROVIDER_REALESTATE')
    },

    openUpdateModalImob(client) {
      this.selectedClient = client
      this.openModal('CREATE_UPDATE_SERVICE_PROVIDER_REALESTATE')
    },

    openCreateModal(client = null) {
      this.selectedClient = client
      this.dialogVisible = true
    },

    openViewContract(client) {
      this.selectedClient = client
      this.openModal('VIEW_CONTRACT_MODAL')
    },

    openAddBalanceModal(client) {
      this.selectedClient = client
      this.openModal('SERVICE_PROVIDER_ADD_BALANCE_MODAL')
    },

    confirmDelete(client) {
      this.selectedClient = client
      this.openModal('REMOVE_SERVICE_PROVIDER_REALESTATE')
    },

    handleError(error) {
      this.error = error
    }
  },

  mounted() {
    this.listServiceProviderCustomers()
  },
}
</script>
